import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { undoToast } from '../app/session';

/** How long the message stays. Long enough to read it, reach for the mouse and
 *  press Undo; short enough that it is not still sitting over the garment when
 *  the next render lands. */
export const TOAST_MS = 6000;

/**
 * The one line the app says out loud about what it just did, at the bottom-left
 * of the viewer — with **Undo** beside it when there is something to undo.
 *
 * Audit #14: the file, ink and colourway × deleted instantly and silently. The ×
 * still deletes instantly (a confirm dialog on every removal is the worse trade),
 * but nothing is lost now: the removal says what went, and the way back is one
 * click or one Ctrl+Z.
 *
 * The live region is always mounted and the bar inside it comes and goes: a
 * `role="status"` added to the page at the same moment as its text is announced
 * unreliably, where one that was already there announces every change. `polite`,
 * so nothing interrupts what a screen reader is already saying, and the focus
 * never moves.
 *
 * Hovering pauses the timer — a shop reaching for Undo must not have the button
 * disappear from under the pointer — and Escape dismisses the bar.
 *
 * The Undo button is bound to the entry the message NAMES (`toast.undoToken`), not
 * to the top of the stack: within the six seconds a shop can nudge the art or
 * toggle an ink, and a button that said "Removed Charcoal — Undo" and then undid
 * the nudge would be lying. Ctrl+Z is the one that always takes the latest change.
 */
export function UndoToast() {
  const toast = useStore((s) => s.toast);
  const dismissToast = useStore((s) => s.dismissToast);
  /** True while the pointer is over the bar. State, not a ref, because a second
   *  message arriving under a resting pointer must also wait: the two occupy the
   *  same place, and the pointer never left. */
  const [paused, setPaused] = useState(false);

  const id = toast?.id;
  useEffect(() => {
    if (id === undefined || paused) return;
    // Dismissed BY ID, never "whatever is on screen": a message that replaced
    // this one has its own six seconds, and this timer must not cut them short.
    const t = setTimeout(() => dismissToast(id), TOAST_MS);
    return () => clearTimeout(t);
  }, [id, paused, dismissToast]);

  useEffect(() => {
    if (id === undefined) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismissToast(id); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id, dismissToast]);

  return (
    <div className="undo-toast-region" role="status" aria-live="polite">
      {toast && (
        <div
          className="undo-toast"
          onPointerEnter={() => setPaused(true)}
          onPointerLeave={() => setPaused(false)}
        >
          <span>{toast.text}</span>
          {toast.undoToken !== null && (
            <>
              <span className="muted" aria-hidden="true">—</span>
              <button type="button" className="undo-toast-btn" onClick={() => undoToast(toast.undoToken!)}>Undo</button>
            </>
          )}
          <button type="button" className="undo-toast-close" aria-label="Dismiss" onClick={() => dismissToast(toast.id)}>×</button>
        </div>
      )}
    </div>
  );
}
